import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Box, Flex, FlexProps, Text, Button } from "@radix-ui/themes";
import { StandardMessage, getAuthorInitials } from "../../entities/Message";
import { MessageAvatarSVG, MessageAvatarSVGProps } from "../loadSVG";
import { SvgRadius } from "../../entities/ChatSettingsV2";
import { MessageText } from "./MessageText";
import {
  Check,
  CheckCheck,
  ArrowRight,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "lucide-react";
import { pointArrowsToElement } from "../lighthouse";
import { useWalkthroughContext } from "../WalkthroughContext";

dayjs.extend(relativeTime);

// localStorage key prefix for walkthrough results
const WALKTHROUGH_RESULTS_STORAGE_KEY = "quackchat/walkthrough_results";

// Helper functions for localStorage
function getWalkthroughResultFromLocalStorage(
  messageKey: string,
): boolean | null {
  try {
    const storedData = localStorage.getItem(
      `${WALKTHROUGH_RESULTS_STORAGE_KEY}/${messageKey}`,
    );
    if (!storedData) {
      return null;
    }
    const parsed = JSON.parse(storedData);
    return parsed === true || parsed === false ? parsed : null;
  } catch (error) {
    console.error("Error reading walkthrough result from localStorage:", error);
    return null;
  }
}

function saveWalkthroughResultToLocalStorage(
  messageKey: string,
  shouldStart: boolean,
) {
  try {
    localStorage.setItem(
      `${WALKTHROUGH_RESULTS_STORAGE_KEY}/${messageKey}`,
      JSON.stringify(shouldStart),
    );
  } catch (error) {
    console.error("Error saving walkthrough result to localStorage:", error);
  }
}

interface WalkthroughProps extends Pick<
  StandardMessage,
  "author" | "deduplicationKey"
> {
  chatSessionId?: string;
  getJourney?: () => any[];
  getDom?: () => string;
  onClose?: () => void;
  isLastMessage: boolean;
  userEmail?: string;
  contextArrowsHelperConfiguration?: {
    isToggleOn: boolean;
    emails: string[];
  };
}

export function useWalkthrough({
  chatSessionId,
  getJourney,
  getDom,
  onClose,
  userEmail,
  contextArrowsHelperConfiguration,
  isLastMessage,
  author,
  deduplicationKey,
}: WalkthroughProps) {
  const { getArrows, isShouldStart } = useWalkthroughContext();

  // Store walkthrough-specific journey events - persists across all walkthrough steps
  const walkthroughJourneyRef = useRef<any[]>([]);
  const previousUrlRef = useRef<string>(window.location.href);
  const navigationHandlerRef = useRef<(() => void) | null>(null);
  const navigationCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Create a wrapper function that merges base journey with walkthrough events
  const getWalkthroughJourney = useCallback(() => {
    const baseJourney = getJourney ? getJourney() : [];
    const mergedJourney = [...baseJourney, ...walkthroughJourneyRef.current];
    return mergedJourney;
  }, [getJourney]);

  // Function to call walkthrough API with optional followup response
  const callWalkthroughAPI = async (responseToFollowupQuestion?: string) => {
    if (!getArrows) {
      console.warn("getContextArrows is not defined");
      return;
    }

    if (!chatSessionId) {
      console.warn("Cannot call walkthrough API: chatSessionId is missing");
      return;
    }

    const journey = getWalkthroughJourney();
    const currentDom = getDom ? getDom() : "";

    console.log("Sending walkthrough API request with:", {
      chatSessionId,
      journeyLength: journey.length,
      journeyEvents: journey,
      domLength: currentDom.length,
      currentUrl: window.location.href,
      responseToFollowupQuestion,
    });

    return getArrows({ journey, currentDom, responseToFollowupQuestion });
  };

  // Use walkthrough context
  const walkthroughContext = useWalkthroughContext();

  // Store the context in a ref so handlers always access the latest state
  const walkthroughContextRef = useRef(walkthroughContext);
  walkthroughContextRef.current = walkthroughContext;

  const arrowsControllerRef = useRef<ReturnType<
    typeof pointArrowsToElement
  > | null>(null);

  // Check if walkthrough button should be shown based on configuration
  const shouldShowWalkthroughFeature = useMemo(() => {
    // Check if arrows feature is enabled
    if (!contextArrowsHelperConfiguration?.isToggleOn) {
      return false;
    }

    // Check email-based access control
    const emails = contextArrowsHelperConfiguration.emails || [];
    if (emails.length > 0) {
      if (!userEmail || !emails.includes(userEmail)) {
        return false;
      }
    }

    return true;
  }, [contextArrowsHelperConfiguration, userEmail]);

  const [isCheckingWalkthrough, setIsCheckingWalkthrough] = useState(false);
  // Store walkthrough results per message key
  const walkthroughResultsRef = useRef<Map<string, boolean>>(new Map());
  // State to track the current message's result (for memoization)
  const [currentMessageResult, setCurrentMessageResult] = useState<
    boolean | undefined
  >(undefined);
  const isCheckingRef = useRef<boolean>(false);
  const currentTargetElementRef = useRef<Element | null>(null);
  const clickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const outsideClickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const targetWasClickedRef = useRef<boolean>(false);
  const isTogglingRef = useRef<boolean>(false); // Prevent race conditions during toggle

  // Memoize whether we should check walkthrough for this message
  const shouldCheckWalkthrough = useMemo(() => {
    // Reset if message is no longer the last one
    if (!isLastMessage) {
      return false;
    }

    // Only check if:
    // 1. isToggleOn is true
    // 2. If emails are configured, userEmail is included
    // 3. Message is from AUTO_AGENT
    if (
      !shouldShowWalkthroughFeature ||
      author !== "AUTO_AGENT" ||
      !chatSessionId
    ) {
      return false;
    }

    // Check if email validation passes
    const emails = contextArrowsHelperConfiguration?.emails || [];
    if (emails.length > 0 && (!userEmail || !emails.includes(userEmail))) {
      return false;
    }

    return true;
  }, [
    isLastMessage,
    shouldShowWalkthroughFeature,
    author,
    chatSessionId,
    userEmail,
    contextArrowsHelperConfiguration,
  ]);

  // Initialize result state when message changes - load from Map and localStorage
  useEffect(() => {
    // First check in-memory Map
    let existingResult = walkthroughResultsRef.current.get(deduplicationKey);

    // If not in Map, check localStorage
    if (existingResult === undefined) {
      const storedResult =
        getWalkthroughResultFromLocalStorage(deduplicationKey);
      if (storedResult !== null) {
        // Restore from localStorage to Map
        walkthroughResultsRef.current.set(deduplicationKey, storedResult);
        existingResult = storedResult;
      }
    }

    // Update state
    if (existingResult !== undefined) {
      setCurrentMessageResult(existingResult);
    } else {
      setCurrentMessageResult(undefined);
    }
  }, [deduplicationKey, chatSessionId]);

  // Check if walkthrough should start via API (only once per message)
  useEffect(() => {
    // Don't check if conditions aren't met
    if (!shouldCheckWalkthrough || !isLastMessage) {
      setIsCheckingWalkthrough(false);
      return;
    }

    // First check in-memory Map
    let existingResult = walkthroughResultsRef.current.get(deduplicationKey);

    // If not in Map, check localStorage
    if (existingResult === undefined) {
      const storedResult =
        getWalkthroughResultFromLocalStorage(deduplicationKey);
      if (storedResult !== null) {
        // Restore from localStorage to Map
        walkthroughResultsRef.current.set(deduplicationKey, storedResult);
        existingResult = storedResult;
        // Sync state
        setCurrentMessageResult(storedResult);
      }
    }

    // If we already have a result (from Map or localStorage), use it and don't call API
    if (existingResult !== undefined) {
      setIsCheckingWalkthrough(false);
      // Ensure state is synced
      if (currentMessageResult !== existingResult) {
        setCurrentMessageResult(existingResult);
      }
      return;
    }

    // If state already has a result, don't call API
    if (currentMessageResult !== undefined) {
      setIsCheckingWalkthrough(false);
      return;
    }

    // If we're still checking, don't call API again
    if (isCheckingRef.current) {
      return;
    }

    const checkWalkthroughShouldStart = async () => {
      walkthroughResultsRef.current.get(deduplicationKey);

      isCheckingRef.current = true;
      setIsCheckingWalkthrough(true);
      try {
        const currentDom = getDom ? getDom() : "";

        if (!chatSessionId) {
          console.warn(
            "Cannot check walkthrough should start: chatSessionId is missing",
          );
          return;
        }
        const body: {
          chatSessionId: string;
          currDom: string;
          userEmail?: string;
        } = {
          chatSessionId,
          currDom: currentDom,
        };

        // Include userEmail if emails are configured
        const emails = contextArrowsHelperConfiguration?.emails || [];
        if (emails.length > 0 && userEmail) {
          body.userEmail = userEmail;
        }

        const response = await isShouldStart({
          currDom: currentDom,
          userEmail,
        });
        const shouldStart = response?.shouldStart === true;

        walkthroughResultsRef.current.set(deduplicationKey, shouldStart);

        saveWalkthroughResultToLocalStorage(deduplicationKey, shouldStart);
        setCurrentMessageResult(shouldStart);
      } catch (error) {
        console.error("Error checking if walkthrough should start:", error);
        // Store false result so we don't call API again for this message
        walkthroughResultsRef.current.set(deduplicationKey, false);
        // Save to localStorage for persistence across chat sessions
        saveWalkthroughResultToLocalStorage(deduplicationKey, false);
        // Update state to trigger re-render
        setCurrentMessageResult(false);
      } finally {
        setIsCheckingWalkthrough(false);
        isCheckingRef.current = false;
      }
    };

    checkWalkthroughShouldStart();
  }, [
    shouldCheckWalkthrough,
    isLastMessage,
    deduplicationKey,
    chatSessionId,
    userEmail,
    contextArrowsHelperConfiguration,
    isShouldStart,
    getDom,
  ]);

  // Memoize button visibility based on result and isLastMessage
  // Once the button appears (result is true), it stays visible as long as message is last
  const showWalkthroughButton = useMemo(() => {
    // Only show if message is still the last one and conditions are met
    if (!isLastMessage || !shouldCheckWalkthrough) {
      return false;
    }
    // Show button only if we have a true result (consistent once it appears)
    return currentMessageResult === true;
  }, [isLastMessage, shouldCheckWalkthrough, currentMessageResult]);

  // Clean up arrows and event listeners on unmount
  useEffect(() => {
    return () => {
      cleanupWalkthrough();
    };
  }, []);

  const cleanupWalkthrough = () => {
    // Remove arrows
    if (arrowsControllerRef.current) {
      arrowsControllerRef.current.remove();
      arrowsControllerRef.current = null;
    }

    // Remove click listeners
    if (clickHandlerRef.current) {
      document.removeEventListener(
        "click",
        clickHandlerRef.current,
        true, // Remove capture phase listener
      );
      clickHandlerRef.current = null;
    }
    currentTargetElementRef.current = null;

    if (outsideClickHandlerRef.current) {
      document.removeEventListener(
        "click",
        outsideClickHandlerRef.current,
        true,
      );
      outsideClickHandlerRef.current = null;
    }

    // Remove navigation listeners
    if (navigationHandlerRef.current) {
      window.removeEventListener("popstate", navigationHandlerRef.current);
      navigationHandlerRef.current = null;
    }

    if (navigationCheckIntervalRef.current) {
      clearInterval(navigationCheckIntervalRef.current);
      navigationCheckIntervalRef.current = null;
    }
  };

  const continueWalkthrough = async (
    incrementStep: boolean = true,
    responseToFollowupQuestion?: string,
  ) => {
    if (!chatSessionId) {
      console.warn("Cannot continue walkthrough: chatSessionId is missing");
      cleanupWalkthrough();
      return;
    }

    // Reset target clicked flag
    targetWasClickedRef.current = false;

    // Clean up previous arrows and listeners (but keep progress bar)
    if (arrowsControllerRef.current) {
      arrowsControllerRef.current.remove();
      arrowsControllerRef.current = null;
    }

    if (clickHandlerRef.current) {
      document.removeEventListener("click", clickHandlerRef.current, true);
      clickHandlerRef.current = null;
    }
    currentTargetElementRef.current = null;

    if (outsideClickHandlerRef.current) {
      document.removeEventListener(
        "click",
        outsideClickHandlerRef.current,
        true,
      );
      outsideClickHandlerRef.current = null;
    }

    if (navigationHandlerRef.current) {
      window.removeEventListener("popstate", navigationHandlerRef.current);
      navigationHandlerRef.current = null;
    }

    if (navigationCheckIntervalRef.current) {
      clearInterval(navigationCheckIntervalRef.current);
      navigationCheckIntervalRef.current = null;
    }

    // Increment step if needed (but don't exceed 4 since we have 5 steps indexed 0-4)
    if (incrementStep) {
      walkthroughContextRef.current.setCurrentStep(
        Math.min(walkthroughContextRef.current.state.currentStep + 1, 4),
      );
    }

    try {
      walkthroughContextRef.current.setIsLoading(true);
      const result = await callWalkthroughAPI(responseToFollowupQuestion);

      // Normalize followup from API (support both snake_case and camelCase)

      const followupQuestionFromApi =
        result?.followup_question ??
        (result as { followupQuestion?: string } | undefined)?.followupQuestion;
      // Only consider we have an index when it's a non-empty value we can use in a selector
      const hasValidIndex =
        result?.index != null && String(result.index).trim() !== "";

      // Ensure topbar is visible when we receive arrows response
      if (hasValidIndex || followupQuestionFromApi) {
        walkthroughContextRef.current.ensureTopbarVisible();
      }

      console.log("followupQuestionFromApi", followupQuestionFromApi);

      console.log("hasValidIndex", hasValidIndex);
      // Update follow-up question only if there's no index (no element to point to)
      // When there's an index, we're pointing to an element and don't show the question
      if (followupQuestionFromApi && !hasValidIndex) {
        walkthroughContextRef.current.setFollowupQuestion(
          followupQuestionFromApi,
        );
      } else {
        walkthroughContextRef.current.setFollowupQuestion(undefined);
      }

      if (hasValidIndex) {
        // Create selector using the data-llm-id attribute
        const selector = `[data-llm-id="${result.index}"]`;
        const targetElement = document.querySelector(selector);

        if (!targetElement) {
          console.warn(`Could not find element with selector: ${selector}`);
          walkthroughContextRef.current.setFollowupQuestion(undefined);
          walkthroughContextRef.current.stopWalkthrough();
          cleanupWalkthrough();
          return;
        }

        console.log("Pointing circle to element:", selector);
        const arrowsController = pointArrowsToElement(selector, {
          color: "#3b82f6", // Blue color
          size: 16,
          distance: 8,
          animationDuration: 1000,
        });

        if (arrowsController) {
          arrowsControllerRef.current = arrowsController;
          currentTargetElementRef.current = targetElement;

          // Add click listener on document level to catch all clicks, including those on portals
          // This ensures we catch clicks even when portals/dropdowns open
          // IMPORTANT: We don't prevent default or stop propagation - let native behavior work
          const handleDocumentClick = async (e: MouseEvent) => {
            // Skip if this is not an Element (e.g., text node, document, etc.)
            if (!(e.target instanceof Element)) {
              return;
            }

            const clickedElement = e.target as Element;

            // Check if click is on target or any of its descendants (including portals)
            const isClickOnTarget =
              targetElement === clickedElement ||
              targetElement.contains(clickedElement) ||
              (clickedElement.closest &&
                clickedElement.closest(`[data-llm-id="${result.index}"]`) ===
                  targetElement);

            if (!isClickOnTarget) {
              return; // Not a click on the target, ignore
            }

            // Mark that target was clicked (to prevent outside click handler from firing)
            targetWasClickedRef.current = true;

            // IMPORTANT: Don't preventDefault or stopPropagation here
            // Let the element's native click behavior work normally
            // We're just observing the click, not intercepting it

            console.log("✅ Target element clicked - continuing walkthrough", {
              targetElement: targetElement,
              clickedElement: clickedElement,
              targetTag: targetElement.tagName,
              clickedTag: clickedElement.tagName,
              targetId: targetElement.id,
              clickedId: clickedElement.id,
              targetDataLlmId: result.index,
              currentUrl: window.location.href,
            });

            // Record click event
            const clickEvent = {
              type: "click",
              timestamp: Date.now(),
              element: {
                tag: clickedElement.tagName.toLowerCase(),
                id: clickedElement.id || undefined,
                dataLlmId: result.index,
                text:
                  clickedElement.textContent?.trim().substring(0, 100) ||
                  targetElement.textContent?.trim().substring(0, 100) ||
                  undefined,
              },
              url: window.location.href,
            };

            walkthroughJourneyRef.current.push(clickEvent);
            console.log("Click event added to journey:", clickEvent);
            console.log(
              "Total walkthrough events:",
              walkthroughJourneyRef.current.length,
            );

            // Store current URL to detect navigation
            const currentUrl = window.location.href;
            const urlBeforeClick = previousUrlRef.current;
            previousUrlRef.current = currentUrl;

            // Set up comprehensive navigation tracking
            const checkNavigation = () => {
              const newUrl = window.location.href;
              if (newUrl !== urlBeforeClick && newUrl !== currentUrl) {
                // Navigation occurred
                const navigationEvent = {
                  type: "navigation",
                  timestamp: Date.now(),
                  from: urlBeforeClick,
                  to: newUrl,
                };
                walkthroughJourneyRef.current.push(navigationEvent);
                previousUrlRef.current = newUrl;
                console.log(
                  "Navigation event added to journey:",
                  navigationEvent,
                );
                console.log(
                  "Total walkthrough events:",
                  walkthroughJourneyRef.current.length,
                );

                // Clear interval once navigation is detected
                if (navigationCheckIntervalRef.current) {
                  clearInterval(navigationCheckIntervalRef.current);
                  navigationCheckIntervalRef.current = null;
                }
              }
            };

            // Remove previous navigation handlers
            if (navigationHandlerRef.current) {
              window.removeEventListener(
                "popstate",
                navigationHandlerRef.current,
              );
            }

            if (navigationCheckIntervalRef.current) {
              clearInterval(navigationCheckIntervalRef.current);
            }

            // Listen for browser navigation (back/forward)
            const handlePopState = () => {
              checkNavigation();
            };
            window.addEventListener("popstate", handlePopState);
            navigationHandlerRef.current = handlePopState;

            // Check for URL changes periodically (for programmatic navigation, form submits, etc.)
            let checkCount = 0;
            navigationCheckIntervalRef.current = setInterval(() => {
              checkNavigation();
              checkCount++;
              // Stop checking after 3 seconds (30 checks at 100ms intervals)
              if (checkCount >= 30) {
                if (navigationCheckIntervalRef.current) {
                  clearInterval(navigationCheckIntervalRef.current);
                  navigationCheckIntervalRef.current = null;
                }
              }
            }, 100);

            // Also check immediately after a short delay
            setTimeout(() => {
              checkNavigation();
            }, 50);

            // Wait a bit for DOM to update (e.g., if click opens a dropdown/list/portal)
            // This ensures getDom() captures the updated DOM state
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Continue walkthrough with updated journey and DOM
            console.log("Continuing walkthrough with updated journey and DOM", {
              journeyEvents: walkthroughJourneyRef.current.length,
              currentUrl: window.location.href,
            });
            await continueWalkthrough();
          };

          // Listen on document to catch all clicks, including those on portals
          document.addEventListener("click", handleDocumentClick, true);
          clickHandlerRef.current = handleDocumentClick;

          // Add click listener to detect clicks outside the target element (blue circle / target)
          // Use capture phase so we run before other handlers; use small delay so target click runs first
          const processOutsideInteraction = (e: MouseEvent) => {
            setTimeout(() => {
              if (isTogglingRef.current) return;
              if (targetWasClickedRef.current) {
                targetWasClickedRef.current = false;
                return;
              }
              if (
                !currentTargetElementRef.current ||
                !arrowsControllerRef.current
              ) {
                return;
              }

              let clickedElement: Element | null = null;
              if (e.composedPath?.().length) {
                for (const pathElement of e.composedPath()) {
                  if (pathElement instanceof Element) {
                    clickedElement = pathElement;
                    break;
                  }
                }
              }
              if (!clickedElement && e.target instanceof Element) {
                clickedElement = e.target;
              }
              if (!clickedElement) return;

              // Ignore clicks on walkthrough progress bar (Stop / Resume)
              if (clickedElement.closest('[data-walkthrough-ui="true"]')) {
                return;
              }

              const currentTarget = currentTargetElementRef.current;
              if (
                currentTarget === clickedElement ||
                (currentTarget && currentTarget.contains(clickedElement)) ||
                (clickedElement.closest &&
                  clickedElement.closest(`[data-llm-id="${result.index}"]`) ===
                    currentTarget)
              ) {
                return; // Click is on target or its children, do not pause
              }

              // Click is outside target → pause and show Resume
              walkthroughContextRef.current.setIsPaused(true);
              walkthroughJourneyRef.current.push({
                type: "walkthrough_stop_outside_click",
                timestamp: Date.now(),
                currentStep: walkthroughContextRef.current.state.currentStep,
                url: window.location.href,
              });
              cleanupWalkthrough();
            }, 10);
          };

          document.addEventListener("click", processOutsideInteraction, true);
          outsideClickHandlerRef.current = processOutsideInteraction;
        } else {
          console.warn(`Could not create arrows for selector: ${selector}`);
        }
      } else if (!followupQuestionFromApi) {
        console.log("followupQuestionFromApi", followupQuestionFromApi);
        // No index and no followup question → walkthrough is complete, stop and close the topbar
        console.log("Walkthrough complete - no more steps");
        walkthroughContextRef.current.stopWalkthrough();
        cleanupWalkthrough();
      }
      // When we have followup question but no index: topbar stays open and shows the followup (nothing else to do here)
    } catch (error) {
      console.error("Error continuing walkthrough:", error);
      cleanupWalkthrough();
    } finally {
      walkthroughContextRef.current.setIsLoading(false);
    }
  };

  const handleWalkthroughClick = async () => {
    console.log("handleWalkthroughClick - Starting walkthrough", {
      chatSessionId,
      initialJourneyLength: walkthroughJourneyRef.current.length,
    });

    if (!chatSessionId) {
      console.warn("Cannot call walkthrough API: chatSessionId is missing");
      return;
    }

    // Close the QuackChat widget
    if (onClose) {
      onClose();
    }

    // Reset URL tracking for new walkthrough session
    previousUrlRef.current = window.location.href;

    // Start walkthrough in context
    walkthroughContextRef.current.startWalkthrough();

    // Start the walkthrough - journey will be sent via getWalkthroughJourney
    // Don't increment step on first call
    await continueWalkthrough(false);
  };

  // Use refs to store the latest handlers so we don't have stale closures
  const handleStopRef = useRef<() => Promise<void>>();
  const handleContinueRef = useRef<() => Promise<void>>();
  const handleSendFollowupResponseRef =
    useRef<(response: string) => Promise<void>>();

  // Update the ref in useEffect to avoid updating mid-execution
  useEffect(() => {
    handleStopRef.current = async () => {
      // Prevent race conditions - if already toggling, ignore
      if (isTogglingRef.current) {
        console.log("Stop - Toggle already in progress, ignoring click");
        return;
      }

      isTogglingRef.current = true;

      try {
        const ctx = walkthroughContextRef.current;
        console.log("Stop button clicked, full state:", {
          isPaused: ctx.state.isPaused,
          isActive: ctx.state.isActive,
          currentStep: ctx.state.currentStep,
        });

        // Record stop event in journey
        const stopEvent = {
          type: "walkthrough_stop",
          timestamp: Date.now(),
          currentStep: ctx.state.currentStep,
          url: window.location.href,
        };
        walkthroughJourneyRef.current.push(stopEvent);
        console.log("Stop event added to journey:", stopEvent);

        console.log("Setting isPaused to true");
        ctx.setIsPaused(true);
        cleanupWalkthrough();
      } finally {
        // Reset the toggling flag after a short delay
        setTimeout(() => {
          isTogglingRef.current = false;
        }, 500);
      }
    };

    handleContinueRef.current = async () => {
      // Prevent race conditions - if already toggling, ignore
      if (isTogglingRef.current) {
        console.log("Continue - Toggle already in progress, ignoring click");
        return;
      }

      isTogglingRef.current = true;

      try {
        const ctx = walkthroughContextRef.current;
        console.log("Continue button clicked, full state:", {
          isPaused: ctx.state.isPaused,
          isActive: ctx.state.isActive,
          currentStep: ctx.state.currentStep,
        });

        // Record continue event in journey
        const continueEvent = {
          type: "walkthrough_continue",
          timestamp: Date.now(),
          currentStep: ctx.state.currentStep,
          url: window.location.href,
        };
        walkthroughJourneyRef.current.push(continueEvent);
        console.log("Continue event added to journey:", continueEvent);

        ctx.setIsPaused(false);
        // Don't increment step - resume from current step
        await continueWalkthrough(false);
      } finally {
        // Reset the toggling flag after a short delay
        setTimeout(() => {
          isTogglingRef.current = false;
        }, 500);
      }
    };

    handleSendFollowupResponseRef.current = async (response: string) => {
      const ctx = walkthroughContextRef.current;

      // Record follow-up response in journey
      const followupResponseEvent = {
        type: "followup_response",
        timestamp: Date.now(),
        currentStep: ctx.state.currentStep,
        question: ctx.state.followupQuestion,
        response: response,
        url: window.location.href,
      };
      walkthroughJourneyRef.current.push(followupResponseEvent);

      // Continue walkthrough with the response
      await continueWalkthrough(true, response);
    };
  });

  // Create stable wrapper functions that call the ref
  const handleStop = useCallback(async () => {
    await handleStopRef.current?.();
  }, []);

  const handleContinue = useCallback(async () => {
    await handleContinueRef.current?.();
  }, []);

  const handleSendFollowupResponse = useCallback(async (response: string) => {
    await handleSendFollowupResponseRef.current?.(response);
  }, []);

  // Register handlers with context (only once since the callbacks are now stable)
  useEffect(() => {
    walkthroughContext.registerHandlers(
      handleStop,
      handleContinue,
      handleSendFollowupResponse,
    );
  }, [
    handleStop,
    handleContinue,
    handleSendFollowupResponse,
    walkthroughContext.registerHandlers,
  ]);

  return {
    showWalkthroughButton,
    shouldShowWalkthroughFeature,
    handleWalkthroughClick,
  };
}

interface StandardMessageComponentProps
  extends StandardMessage, WalkthroughProps {
  customSVG: string;
  userAvatarSVG?: string;
  showUserBackground?: boolean;
  svgRadius?: SvgRadius;
  showAvatar: boolean;
  showInitials?: boolean;
  showTimestamp: boolean;
  orientation: "left" | "right";
  thinking: boolean;
  isLastMessage: boolean;
  quackAgentName: string;
  track?: (eventName: string, properties: any) => void;
}

export function StandardMessageComponent({
  deduplicationKey,
  author,
  text,
  data: { agentDisplayName },
  createdAt,
  messageScore,
  showInitials = false,
  showAvatar,
  svgRadius,
  showTimestamp,
  orientation,
  customSVG,
  userAvatarSVG,
  showUserBackground = false,
  isLastMessage,
  quackAgentName,
  chatSessionId,
  getJourney,
  getDom,
  onClose,
  userEmail,
  contextArrowsHelperConfiguration,
  track,
}: StandardMessageComponentProps) {
  const {
    showWalkthroughButton,
    shouldShowWalkthroughFeature,
    handleWalkthroughClick,
  } = useWalkthrough({
    chatSessionId,
    getJourney,
    getDom,
    onClose,
    userEmail,
    contextArrowsHelperConfiguration,
    isLastMessage,
    author,
    deduplicationKey,
  });

  const showMessageFooter = showTimestamp || messageScore != null;
  const initials = getAuthorInitials(agentDisplayName);
  const isPositiveMessageScore = messageScore != null && messageScore >= 3;

  const [readStatus, setReadStatus] = useState<"sent" | "delievered" | "seen">(
    isLastMessage ? "sent" : "seen",
  );

  useEffect(() => {
    if (isLastMessage && author === "USER") {
      setTimeout(() => {
        setReadStatus("delievered");
      }, 2000);

      setTimeout(() => {
        setReadStatus("seen");
      }, 6000);
    }
  }, []);

  const flexProps = useMemo<FlexProps>(() => {
    return {
      pl: showAvatar && orientation === "left" ? "0" : "36px",
      pr: showAvatar && orientation === "right" ? "0" : "36px",
    };
  }, [showAvatar, orientation]);

  const textBoxStyle = useMemo<CSSProperties>(() => {
    if (author === "USER") {
      if (showUserBackground) {
        return {
          color: "var(--userText)",
          background: "var(--userBackground)",
          padding: "8px 12px",
          borderRadius: "8px",
          position: "relative",
        };
      }

      return {
        color: "var(--userText)",
        padding: "0px 12px 12px",
        borderRadius: "8px",
      };
    } else if (author === "LIVE_AGENT") {
      return {
        color: "var(--agentText)",
        background: "var(--agentBackground)",
        padding: "8px 12px",
        borderRadius: "8px",
        position: "relative",
      };
    } else {
      return {
        background:
          "linear-gradient(260.74deg, rgba(249, 243, 227, 0.35) 4.89%, rgba(226, 234, 255, 0.35) 101.01%)",
        padding: "8px 12px",
        borderRadius: "8px",
        position: "relative",
      };
    }
  }, [author, showUserBackground]);

  const avatarProps = useMemo<MessageAvatarSVGProps>(() => {
    const baseProps: MessageAvatarSVGProps = {
      radius: svgRadius,
      cssOverrides: showMessageFooter
        ? {
            marginBottom: "36px",
          }
        : undefined,
    };

    if (author === "USER") {
      return {
        ...baseProps,
        kind: "user",
        initials: showInitials ? initials : undefined,
        path: userAvatarSVG,
        alt: "User Avatar",
      };
    } else if (author === "LIVE_AGENT") {
      return {
        ...baseProps,
        kind: "agent",
        initials: showInitials ? initials : undefined,
        alt: `${agentDisplayName} Avatar`,
      };
    } else {
      return {
        ...baseProps,
        path: customSVG,
        alt: `${quackAgentName} Avatar`,
      };
    }
  }, [author, initials, showInitials, customSVG, svgRadius, showMessageFooter]);

  return (
    <Flex
      key={deduplicationKey}
      gap={"3"}
      align={"end"}
      direction={orientation === "right" ? "row-reverse" : "row"}
      {...flexProps}
    >
      {showAvatar && <MessageAvatarSVG {...avatarProps} />}

      <Flex
        direction={"column"}
        align={orientation === "right" ? "end" : "start"}
        gap={"2"}
      >
        <Box id={deduplicationKey} style={textBoxStyle}>
          <MessageText text={text} track={track} />
        </Box>

        {showMessageFooter && (
          <Flex
            minHeight={"28xwpx"}
            direction={"row"}
            gap={"2"}
            align={"start"}
            data-message-footer={deduplicationKey}
          >
            {showTimestamp && (
              <Text size={"1"} color={"gray"}>
                {dayjs().diff(dayjs(createdAt), "day") > 1
                  ? dayjs(createdAt).format("hh:mm:ss A")
                  : dayjs(createdAt).fromNow()}
              </Text>
            )}

            {messageScore != null &&
              (isPositiveMessageScore ? (
                <ThumbsUpIcon
                  width={"14"}
                  height={"14"}
                  color="var(--green-9)"
                />
              ) : (
                <ThumbsDownIcon
                  width={"14"}
                  height={"14"}
                  color="var(--red-9)"
                />
              ))}

            {author === "USER" && (
              <>
                {isLastMessage ? (
                  <>
                    {readStatus === "sent" && <Check size={16} />}
                    {readStatus === "delievered" && <CheckCheck size={16} />}
                    {readStatus === "seen" && (
                      <CheckCheck size={16} color="#00b700" />
                    )}
                  </>
                ) : (
                  <CheckCheck size={16} color="#00b700" />
                )}
              </>
            )}
          </Flex>
        )}

        {author === "AUTO_AGENT" &&
          isLastMessage &&
          showWalkthroughButton &&
          shouldShowWalkthroughFeature && (
            <Flex mt={"1"}>
              <Button
                size="1"
                variant="solid"
                color="indigo"
                onClick={handleWalkthroughClick}
                style={{
                  fontSize: "11px",
                  padding: "4px 8px",
                  height: "auto",
                }}
              >
                Start Walkthrough{" "}
                <ArrowRight size={14} style={{ marginLeft: "4px" }} />
              </Button>
            </Flex>
          )}
      </Flex>
    </Flex>
  );
}
